import type { OwnerScopeProps } from '@/components/shared/ownerScope';
import type {
  ChallengeStack,
  StackChallengeSummary,
} from '@/config/objects/placements';
import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Layers,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export interface StacksManagerProps extends OwnerScopeProps {
  /**
   * Map the raw `${apiBase}/challenges` response items into the stack pool
   * shape. Event challenges already match `StackChallengeSummary`; template
   * challenge links need their display name pulled from
   * `link.libraryChallenge.name`. Defaults to a pass-through cast.
   */
  normalizeChallenges?: (items: unknown[]) => StackChallengeSummary[];
}

const defaultNormalizeChallenges = (items: unknown[]): StackChallengeSummary[] =>
  items as StackChallengeSummary[];

/**
 * Owner-scoped manager for authoring DT-170 challenge stacks. Each stack
 * groups multiple challenges that share a single GPS location and reveal
 * sequentially. Admins create the stack with a name + coordinates, then
 * pick which challenges belong to it and in what order. The children
 * stay in their own challenge groups for unlock purposes — the stack
 * just layers "wait for previous in stack" on top.
 *
 * Serves both events (`apiBase=/events/:id`, `keyPrefix='Event'` — query
 * keys and URLs are byte-identical to the original EventStacksTab) and
 * templates (`apiBase=/templates/:id`, `keyPrefix='Template'`).
 */
export function StacksManager({
  ownerId,
  apiBase,
  keyPrefix,
  structureEditable,
  normalizeChallenges = defaultNormalizeChallenges,
}: StacksManagerProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ChallengeStack | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ChallengeStack | null>(
    null,
  );

  const stacksKey = [`${keyPrefix}Stacks`, ownerId];
  const challengesKey = [`${keyPrefix}Challenges-summary`, ownerId];
  const ownerNoun = keyPrefix === 'Event' ? 'event' : 'template';

  const { data: stacksResp, isLoading: stacksLoading } = useQuery<{
    success: boolean;
    data: ChallengeStack[];
  }>({
    queryKey: stacksKey,
    queryFn: () => apiClient.get(`${apiBase}/stacks`),
    enabled: !!ownerId,
  });
  const stacks = stacksResp?.data ?? [];

  // All owner challenges — used as the "available to add" pool in the
  // edit dialog and to render the add-challenge picker.
  const { data: challengesResp } = useQuery<{
    success: boolean;
    data: unknown[];
  }>({
    queryKey: challengesKey,
    queryFn: () => apiClient.get(`${apiBase}/challenges`),
    enabled: !!ownerId,
  });
  const challenges = useMemo(
    () => normalizeChallenges(challengesResp?.data ?? []),
    [challengesResp, normalizeChallenges],
  );
  const challengeNameById = useMemo(
    () => new Map(challenges.map((c) => [c.id, c.name])),
    [challenges],
  );

  const createMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      latitude: number;
      longitude: number;
    }) => apiClient.post(`${apiBase}/stacks`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stacksKey });
      toast.success('Stack created');
      setShowCreate(false);
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to create stack'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      stackId,
      data,
    }: {
      stackId: string;
      data: Record<string, unknown>;
    }) => apiClient.patch(`${apiBase}/stacks/${stackId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stacksKey });
      toast.success('Stack updated');
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to update stack'),
  });

  const setChildrenMutation = useMutation({
    mutationFn: async ({
      stackId,
      challengeIds,
    }: {
      stackId: string;
      challengeIds: string[];
    }) =>
      apiClient.put(`${apiBase}/stacks/${stackId}/challenges`, {
        challengeIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stacksKey });
      queryClient.invalidateQueries({ queryKey: challengesKey });
      toast.success('Stack updated');
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to update stack'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (stackId: string) =>
      apiClient.delete(`${apiBase}/stacks/${stackId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stacksKey });
      queryClient.invalidateQueries({ queryKey: challengesKey });
      toast.success('Stack deleted');
      setPendingDelete(null);
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to delete stack'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Challenge Stacks</h2>
          <p className="text-sm text-muted-foreground">
            Group challenges at a shared location into a sequential reveal —
            mobile shows one map pin and unlocks the next challenge once the
            current one is solved.
          </p>
        </div>
        {structureEditable && (
          <Button onClick={() => setShowCreate(true)} size="sm" icon={Plus}>
            New Stack
          </Button>
        )}
      </div>

      {stacksLoading && (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      )}

      {!stacksLoading && stacks.length === 0 && (
        <div className="text-center py-12 px-4 border-2 border-dashed rounded-xl">
          <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">No stacks yet</h3>
          <p className="text-muted-foreground mb-4">
            {structureEditable
              ? 'Create a stack at a notable location, then add 2–5 challenges that play sequentially there.'
              : `This ${ownerNoun} has no stacks configured.`}
          </p>
          {structureEditable && (
            <Button onClick={() => setShowCreate(true)} icon={Plus}>
              New Stack
            </Button>
          )}
        </div>
      )}

      {stacks.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {stacks.map((stack) => (
            <Card key={stack.id} className="gap-0 rounded-xl border bg-card py-0 shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Layers className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-sm truncate">{stack.name}</h3>
                    <p className="text-xs text-muted-foreground font-mono">
                      {stack.latitude.toFixed(5)}, {stack.longitude.toFixed(5)}
                    </p>
                  </div>
                  <span className="font-mono text-sm font-semibold tabular-nums text-primary">
                    {stack.challenges.length}
                  </span>
                </div>

                {stack.challenges.length > 0 ? (
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    {stack.challenges.map((c) => (
                      <li key={c.id} className="truncate">
                        {c.name}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-xs italic text-muted-foreground">
                    No challenges yet
                  </p>
                )}

                {structureEditable && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditing(stack)}
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => setPendingDelete(stack)}
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateStackDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSubmit={(payload) => createMutation.mutate(payload)}
        loading={createMutation.isPending}
      />

      <EditStackDialog
        stack={editing}
        challenges={challenges}
        challengeNameById={challengeNameById}
        onClose={() => setEditing(null)}
        onSaveDetails={(data) => {
          if (!editing) return;
          updateMutation.mutate({ stackId: editing.id, data });
        }}
        onSaveChildren={(challengeIds) => {
          if (!editing) return;
          setChildrenMutation.mutate({ stackId: editing.id, challengeIds });
        }}
        savingDetails={updateMutation.isPending}
        savingChildren={setChildrenMutation.isPending}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete Stack"
        description={`Delete "${pendingDelete?.name}"? Children will keep existing as ungrouped challenges; only the stack itself is removed.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
      />
    </div>
  );
}

function CreateStackDialog({
  open,
  onOpenChange,
  onSubmit,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: { name: string; latitude: number; longitude: number }) => void;
  loading?: boolean;
}) {
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;
    onSubmit({ name: name.trim(), latitude: latNum, longitude: lngNum });
    setName('');
    setLat('');
    setLng('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Stack</DialogTitle>
          <DialogDescription>
            Pick a name and the GPS location where the stack lives. You can
            add challenges to it after it's created.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FormFieldGroup name="name" label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Boston Common Trio"
            />
          </FormFieldGroup>
          <div className="grid gap-4 grid-cols-2">
            <FormFieldGroup name="lat" label="Latitude">
              <Input
                type="number"
                step="any"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="40.7128"
              />
            </FormFieldGroup>
            <FormFieldGroup name="lng" label="Longitude">
              <Input
                type="number"
                step="any"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="-74.0060"
              />
            </FormFieldGroup>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={!name.trim() || !lat || !lng}
            >
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditStackDialog({
  stack,
  challenges,
  challengeNameById,
  onClose,
  onSaveDetails,
  onSaveChildren,
  savingDetails,
  savingChildren,
}: {
  stack: ChallengeStack | null;
  challenges: StackChallengeSummary[];
  challengeNameById: Map<string, string>;
  onClose: () => void;
  onSaveDetails: (data: Record<string, unknown>) => void;
  onSaveChildren: (challengeIds: string[]) => void;
  savingDetails?: boolean;
  savingChildren?: boolean;
}) {
  const [name, setName] = useState(stack?.name ?? '');
  const [lat, setLat] = useState(stack?.latitude.toString() ?? '');
  const [lng, setLng] = useState(stack?.longitude.toString() ?? '');
  const [childIds, setChildIds] = useState<string[]>(
    stack?.challenges.map((c) => c.id) ?? [],
  );

  // Re-sync local state whenever the dialog opens for a different
  // stack. Without this, switching between stacks would show stale
  // field values from the previously-edited stack.
  const stackId = stack?.id;
  useEffect(() => {
    setName(stack?.name ?? '');
    setLat(stack?.latitude.toString() ?? '');
    setLng(stack?.longitude.toString() ?? '');
    setChildIds(stack?.challenges.map((c) => c.id) ?? []);
    // Tracking only the stack identity; the rest of the stack object
    // changes when the parent re-fetches and we don't want to clobber
    // in-flight edits at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackId]);

  if (!stack) return null;

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setChildIds((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };
  const moveDown = (idx: number) => {
    setChildIds((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };
  const removeChild = (id: string) => {
    setChildIds((prev) => prev.filter((c) => c !== id));
  };
  const addChild = (id: string) => {
    if (!id || childIds.includes(id)) return;
    setChildIds((prev) => [...prev, id]);
  };

  // Pool of challenges that can still be added — exclude any that are
  // already in this stack OR in another stack (only one stack
  // membership at a time).
  const eligibleToAdd = challenges.filter(
    (c) =>
      !childIds.includes(c.id) &&
      (c.stackId === null || c.stackId === undefined || c.stackId === stack.id),
  );

  const handleSaveDetails = () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    onSaveDetails({
      name: name.trim(),
      latitude: Number.isFinite(latNum) ? latNum : stack.latitude,
      longitude: Number.isFinite(lngNum) ? lngNum : stack.longitude,
    });
  };

  return (
    <Dialog open={!!stack} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit &quot;{stack.name}&quot;</DialogTitle>
          <DialogDescription>
            Update the stack&apos;s details and reorder its challenges.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Details</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveDetails}
                loading={savingDetails}
              >
                Save Details
              </Button>
            </div>
            <FormFieldGroup name="editName" label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormFieldGroup>
            <div className="grid gap-4 grid-cols-2">
              <FormFieldGroup name="editLat" label="Latitude">
                <Input
                  type="number"
                  step="any"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                />
              </FormFieldGroup>
              <FormFieldGroup name="editLng" label="Longitude">
                <Input
                  type="number"
                  step="any"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                />
              </FormFieldGroup>
            </div>
          </section>

          <section className="space-y-3 border-t pt-6">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">
                Challenges in stack ({childIds.length})
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSaveChildren(childIds)}
                loading={savingChildren}
              >
                Save Order
              </Button>
            </div>

            {childIds.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No challenges in this stack yet — pick from below.
              </p>
            )}

            <ol className="space-y-1">
              {childIds.map((id, idx) => (
                <li
                  key={id}
                  className="flex items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2"
                >
                  <span className="text-xs font-mono text-muted-foreground w-5">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm truncate">
                    {challengeNameById.get(id) ?? id}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={idx === 0}
                    onClick={() => moveUp(idx)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={idx === childIds.length - 1}
                    onClick={() => moveDown(idx)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive"
                    onClick={() => removeChild(id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ol>

            {eligibleToAdd.length > 0 && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Add a challenge
                </label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value=""
                  onChange={(e) => addChild(e.target.value)}
                >
                  <option value="">Select…</option>
                  {eligibleToAdd.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </section>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button variant="ghost" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
